'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { actionParentById, isActionKey, menuCatalog, menuTitleById, toggleMenuKey } from '@/config/menu-catalog';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateMenuRoleSet,
  useUpdateMenuRoleSet,
} from '@/hooks/useMenuRoleSet';
import type { MenuRoleSet } from '@/types/menu-role-set';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface RoleSetFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null이면 신규 생성, 값이 있으면 수정 */
  roleSet: MenuRoleSet | null;
  onSaved: () => void;
}

export function RoleSetFormDialog({
  open,
  onOpenChange,
  roleSet,
  onSaved,
}: RoleSetFormDialogProps) {
  const { toast } = useToast();
  const { createMenuRoleSet, loading: creating } = useCreateMenuRoleSet();
  const { updateMenuRoleSet, loading: updating } = useUpdateMenuRoleSet();
  const isSubmitting = creating || updating;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // 다이얼로그가 열릴 때 폼 초기화(수정 시 기존 값 적재)
  useEffect(() => {
    if (open) {
      setName(roleSet?.name ?? '');
      setDescription(roleSet?.description ?? '');
      setSelectedKeys(new Set(roleSet?.menuKeys ?? []));
    }
  }, [open, roleSet]);

  const toggleItem = (id: string, checked: boolean) => {
    setSelectedKeys(prev => toggleMenuKey(prev, id, checked));
  };

  /** 섹션 전체 선택/해제는 화면 키만 다룬다. 동작 권한은 옵트인이라 개별 클릭으로만 켜진다(해제는 부모를 따라간다). */
  const toggleSection = (screenIds: string[], checked: boolean) => {
    setSelectedKeys(prev => screenIds.reduce((acc, id) => toggleMenuKey(acc, id, checked), prev));
  };

  // 카운트는 카탈로그에 있는 키만 센다. DB에만 있는 키(운영 DML 등)는 보이지 않지만 저장 시 그대로 보존된다.
  const screenCount = menuCatalog.flatMap(s => s.items).filter(item => selectedKeys.has(item.id)).length;
  const actionCount = Array.from(selectedKeys).filter(isActionKey).length;
  // 고아 동작(운영 DML 등으로 부모 없이 들어온 키): 열 때 자동으로 고치지 않고 표시만 한다.
  // 관리자가 부모를 켜거나 동작을 끄면 풀린다. 서버도 같은 규칙으로 400을 돌려준다.
  const orphanActions = Array.from(selectedKeys).filter(
    key => isActionKey(key) && !selectedKeys.has(actionParentById[key])
  );

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: '입력 오류',
        description: '세트 이름을 입력해주세요',
        variant: 'destructive',
      });
      return;
    }

    const request = {
      name: name.trim(),
      description: description.trim() || undefined,
      menuKeys: Array.from(selectedKeys),
    };

    try {
      if (roleSet) {
        await updateMenuRoleSet(roleSet.id, request);
        toast({ title: '수정 완료', description: '역할세트가 수정되었습니다' });
      } else {
        await createMenuRoleSet(request);
        toast({ title: '생성 완료', description: '역할세트가 생성되었습니다' });
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast({
        title: roleSet ? '수정 실패' : '생성 실패',
        description: error instanceof Error ? error.message : '역할세트 저장 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{roleSet ? '역할세트 수정' : '새 역할세트'}</DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='role-set-name'>이름</Label>
            <Input
              id='role-set-name'
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='예: 일반 사용자'
            />
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='role-set-description'>설명</Label>
            <Textarea
              id='role-set-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='세트 설명 (선택)'
              rows={2}
            />
          </div>

          <div className='space-y-2'>
            <Label>메뉴 권한</Label>
            <div className='border rounded-lg divide-y'>
              {menuCatalog.map(section => {
                const ids = section.items.map(item => item.id);
                const selected = ids.filter(id => selectedKeys.has(id)).length;
                const all = selected === ids.length && ids.length > 0;
                const some = selected > 0 && !all;
                return (
                  <div key={section.sectionId} className='p-3'>
                    <label className='flex items-center gap-2 font-medium cursor-pointer'>
                      <Checkbox
                        checked={all || (some && 'indeterminate')}
                        onCheckedChange={checked =>
                          toggleSection(ids, checked === true)
                        }
                      />
                      <span>{section.sectionTitle}</span>
                    </label>
                    <div className='mt-2 ml-6 grid grid-cols-2 gap-y-2 gap-x-4'>
                      {section.items.map(item => (
                        // 화면 항목과 그 동작 권한을 한 셀에 묶어 2열 흐름을 유지한다
                        <div key={item.id} className='flex flex-col gap-1'>
                          <label className='flex items-center gap-2 text-sm cursor-pointer'>
                            <Checkbox
                              checked={selectedKeys.has(item.id)}
                              onCheckedChange={checked =>
                                toggleItem(item.id, checked === true)
                              }
                            />
                            <span>{item.title}</span>
                          </label>
                          {item.actions?.map(action => {
                            const orphan =
                              selectedKeys.has(action.id) && !selectedKeys.has(item.id);
                            return (
                              <label
                                key={action.id}
                                className='ml-6 flex items-center gap-2 text-sm cursor-pointer'
                              >
                                <span className='text-xs text-muted-foreground'>└ 추가 권한</span>
                                <Checkbox
                                  checked={selectedKeys.has(action.id)}
                                  onCheckedChange={checked =>
                                    toggleItem(action.id, checked === true)
                                  }
                                />
                                <span>{action.title}</span>
                                {orphan && (
                                  <Badge
                                    variant='outline'
                                    className='border-amber-500 text-amber-700'
                                  >
                                    화면 권한 없음
                                  </Badge>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='text-sm text-muted-foreground'>
              메뉴 {screenCount} · 추가 권한 {actionCount}
            </div>
            {orphanActions.length > 0 && (
              <div className='text-sm text-amber-700'>
                {orphanActions
                  .map(
                    key =>
                      `${menuTitleById[actionParentById[key]]}을(를) 선택하거나 ${menuTitleById[key]}을(를) 해제하세요`
                  )
                  .join(' / ')}
              </div>
            )}
          </div>

          <div className='flex justify-end gap-2 pt-2'>
            <Button
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button onClick={handleSave} disabled={isSubmitting || orphanActions.length > 0}>
              {isSubmitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  저장 중...
                </>
              ) : (
                <>
                  <CheckCircle className='mr-2 h-4 w-4' />
                  저장
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
